// Web-specific Enhanced Progress Metrics Component
// Displays detailed challenge progress information including timeline, points, and daily progress

import React from 'react';
import { useTheme } from '../contexts/ThemeContext';
import {
    ChallengeData,
    ChallengeProgressData,
    calculateDetailedProgressMetrics,
    formatProgressMetrics,
    formatValue,
    getUnitDisplay
} from '@fitzaa/shared';

export interface EnhancedProgressMetricsWebProps {
    challenge: ChallengeData;
    progressData: ChallengeProgressData;
    className?: string;
    showDetailedBreakdown?: boolean;
}

export const EnhancedProgressMetricsWeb = ({
    challenge,
    progressData,
    className = '',
    showDetailedBreakdown = true
}: EnhancedProgressMetricsWebProps) => {
    const theme = useTheme();
    const metrics = calculateDetailedProgressMetrics(challenge, progressData);
    const formattedMetrics = formatProgressMetrics(challenge, progressData);
    const unit = (challenge as any).measurementUnit || challenge.targetUnit || getUnitDisplay(challenge.metric);

    return (
        <div className={`enhanced-progress-metrics ${className}`} style={{
            background: 'transparent',
            borderRadius: '16px',
            padding: '0'
        }}>

            {/* Ultra-Posh Timeline Section */}
            <div style={{
                background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(59, 130, 246, 0.02) 100%)',
                borderRadius: '12px',
                padding: '12px',
                marginBottom: '12px',
                border: '1px solid rgba(59, 130, 246, 0.1)',
                position: 'relative',
                overflow: 'hidden'
            }}>

                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '12px'
                }}>

                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}>
                        <div style={{
                            width: '8px',
                            height: '8px',
                            background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                            borderRadius: '50%',
                            boxShadow: '0 0 8px rgba(59, 130, 246, 0.4)'
                        }} />
                        <span style={{
                            fontSize: '13px',
                            fontWeight: '600',
                            color: theme.theme.colors.text,
                            letterSpacing: '-0.2px'
                        }}>
                            Timeline Progress
                        </span>
                    </div>
                    <div style={{
                        background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                        color: 'white',
                        padding: '4px 10px',
                        borderRadius: '8px',
                        fontSize: '11px',
                        fontWeight: '600',
                        boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)'
                    }}>
                        {metrics.timeline.daysElapsed} of {metrics.timeline.totalDays} days
                    </div>
                </div>
                <div style={{ position: 'relative' }}>
                    <div style={{
                        width: '100%',
                        height: '8px',
                        background: 'rgba(59, 130, 246, 0.1)',
                        borderRadius: '4px',
                        overflow: 'hidden',
                        position: 'relative'
                    }}>
                        <div
                            style={{
                                height: '100%',
                                background: 'linear-gradient(90deg, #3b82f6 0%, #1d4ed8 100%)',
                                borderRadius: '4px',
                                transition: 'width 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
                                width: `${Math.min(100, metrics.timeline.progressPercentage)}%`,
                                boxShadow: '0 0 12px rgba(59, 130, 246, 0.4)'
                            }}
                        />
                    </div>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginTop: '8px',
                        fontSize: '11px',
                        color: theme.theme.colors.textSecondary,
                        fontWeight: '500'
                    }}>
                        <span>Start</span>
                        <span style={{
                            background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                            color: 'white',
                            padding: '3px 8px',
                            borderRadius: '6px',
                            fontWeight: '600',
                            fontSize: '10px'
                        }}>
                            {metrics.timeline.daysRemaining} days left
                        </span>
                        <span>End</span>
                    </div>
                </div>
            </div>
            {/* Ultra-Posh Points Section */}
            <div style={{
                background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.08) 0%, rgba(34, 197, 94, 0.03) 100%)',
                borderRadius: '12px',
                padding: '12px',
                marginBottom: '12px',
                border: '1px solid rgba(34, 197, 94, 0.15)',
                position: 'relative',
                overflow: 'hidden'
            }}>
                {/* Decorative Elements */}
                <div style={{
                    position: 'absolute',
                    top: '-20px',
                    right: '-20px',
                    width: '80px',
                    height: '80px',
                    background: 'radial-gradient(circle, rgba(34, 197, 94, 0.1) 0%, transparent 70%)',
                    borderRadius: '50%'
                }} />

                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '12px'
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}>
                        <div style={{
                            width: '12px',
                            height: '12px',
                            background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                            borderRadius: '50%',
                            boxShadow: '0 0 12px rgba(34, 197, 94, 0.5)',
                            position: 'relative'
                        }}>
                            <div style={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                width: '6px',
                                height: '6px',
                                background: 'white',
                                borderRadius: '50%',
                                opacity: 0.8
                            }} />
                        </div>
                        <span style={{
                            fontSize: '13px',
                            fontWeight: '600',
                            color: theme.theme.colors.text,
                            letterSpacing: '-0.2px'
                        }}>
                            Total Points Earned
                        </span>
                    </div>

                    {/* Ultra-Posh Points Display */}
                    <div style={{
                        background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                        color: 'white',
                        padding: '8px 16px',
                        borderRadius: '12px',
                        fontSize: '14px',
                        fontWeight: '700',
                        boxShadow: '0 4px 12px rgba(34, 197, 94, 0.3)',
                        position: 'relative',
                        overflow: 'hidden'
                    }}>
                        {/* Shine Effect */}
                        <div style={{
                            position: 'absolute',
                            top: '0',
                            left: '-100%',
                            width: '100%',
                            height: '100%',
                            background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
                            animation: 'shine 2s infinite'
                        }} />

                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            position: 'relative',
                            zIndex: 1
                        }}>
                            <span style={{
                                fontSize: '16px',
                                filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))'
                            }}>
                                ⭐
                            </span>
                            <span>{metrics.points.totalEarned}</span>
                            <span style={{
                                fontSize: '10px',
                                opacity: 0.9,
                                fontWeight: '600'
                            }}>
                                points
                            </span>
                        </div>
                    </div>
                </div>

                {/* Points Breakdown */}
                <div style={{
                    background: 'rgba(34, 197, 94, 0.05)',
                    borderRadius: '8px',
                    padding: '8px 12px',
                    border: '1px solid rgba(34, 197, 94, 0.1)'
                }}>
                    <div style={{
                        fontSize: '11px',
                        color: theme.theme.colors.text,
                        fontWeight: '500',
                        lineHeight: '1.4'
                    }}>
                        {(() => {
                            const completedDays = metrics.overallProgress.completedDays;
                            const partialDays = Object.keys(progressData.dailyCompletions || {}).filter(date => {
                                const day = progressData.dailyCompletions![date];
                                return day.value > 0 && !day.isCompleted;
                            }).length;

                            if (completedDays > 0 && partialDays > 0) {
                                return `* ${completedDays} days completed, ${partialDays} days with partial progress (points awarded proportionally)`;
                            } else if (completedDays > 0) {
                                return `* ${completedDays} days completed`;
                            } else if (partialDays > 0) {
                                return `* ${partialDays} days with partial progress (points awarded proportionally)`;
                            } else {
                                return `* Each day is ${metrics.points.pointsPerDay} points`;
                            }
                        })()}
                    </div>
                </div>
            </div>

            {/* Ultra-Posh Overall Progress Section */}
            <div style={{
                background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.08) 0%, rgba(59, 130, 246, 0.03) 100%)',
                borderRadius: '12px',
                padding: '12px',
                marginBottom: '12px',
                border: '1px solid rgba(59, 130, 246, 0.15)',
                position: 'relative',
                overflow: 'hidden'
            }}>
                {/* Decorative Elements */}
                <div style={{
                    position: 'absolute',
                    top: '-20px',
                    left: '-20px',
                    width: '80px',
                    height: '80px',
                    background: 'radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, transparent 70%)',
                    borderRadius: '50%'
                }} />

                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '12px'
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}>
                        <div style={{
                            width: '12px',
                            height: '12px',
                            background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                            borderRadius: '50%',
                            boxShadow: '0 0 12px rgba(59, 130, 246, 0.5)',
                            position: 'relative'
                        }}>
                            <div style={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                width: '6px',
                                height: '6px',
                                background: 'white',
                                borderRadius: '50%',
                                opacity: 0.8
                            }} />
                        </div>
                        <span style={{
                            fontSize: '13px',
                            fontWeight: '600',
                            color: theme.theme.colors.text,
                            letterSpacing: '-0.2px'
                        }}>
                            Total Progress
                        </span>
                    </div>

                    {/* Ultra-Posh Progress Display */}
                    <div style={{
                        background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                        color: 'white',
                        padding: '8px 16px',
                        borderRadius: '12px',
                        fontSize: '14px',
                        fontWeight: '700',
                        boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
                        position: 'relative',
                        overflow: 'hidden'
                    }}>
                        {/* Shine Effect */}
                        <div style={{
                            position: 'absolute',
                            top: '0',
                            left: '-100%',
                            width: '100%',
                            height: '100%',
                            background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
                            animation: 'shine 2s infinite'
                        }} />

                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            position: 'relative',
                            zIndex: 1
                        }}>
                            <span style={{
                                fontSize: '16px',
                                filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))'
                            }}>
                                🎯
                            </span>
                            <span>{metrics.points.completionRate.toFixed(1)}%</span>
                        </div>
                    </div>
                </div>

                {/* Progress Details */}
                <div style={{
                    background: 'rgba(59, 130, 246, 0.05)',
                    borderRadius: '8px',
                    padding: '8px 12px',
                    border: '1px solid rgba(59, 130, 246, 0.1)'
                }}>
                    <div style={{
                        fontSize: '11px',
                        color: theme.theme.colors.text,
                        fontWeight: '600',
                        marginBottom: '4px'
                    }}>
                        {metrics.points.totalEarned} / {metrics.points.totalPossible} points
                    </div>
                    <div style={{
                        fontSize: '10px',
                        color: theme.theme.colors.textSecondary,
                        fontWeight: '500'
                    }}>
                        * {metrics.timeline.totalDays} days × {metrics.points.pointsPerDay} points = {metrics.points.totalPossible} total
                    </div>
                </div>
            </div>

            {/* Ultra-Posh Today's Progress Section */}
            <div style={{
                background: `linear-gradient(135deg, ${metrics.todayProgress.isCompleted
                    ? 'rgba(34, 197, 94, 0.08) 0%, rgba(34, 197, 94, 0.03) 100%'
                    : 'rgba(249, 115, 22, 0.08) 0%, rgba(249, 115, 22, 0.03) 100%'
                    }`,
                borderRadius: '12px',
                padding: '12px',
                marginBottom: '12px',
                border: `1px solid ${metrics.todayProgress.isCompleted
                    ? 'rgba(34, 197, 94, 0.15)'
                    : 'rgba(249, 115, 22, 0.15)'
                    }`,
                position: 'relative',
                overflow: 'hidden'
            }}>
                {/* Decorative Elements */}
                <div style={{
                    position: 'absolute',
                    top: '-20px',
                    right: '-20px',
                    width: '80px',
                    height: '80px',
                    background: `radial-gradient(circle, ${metrics.todayProgress.isCompleted
                        ? 'rgba(34, 197, 94, 0.1)'
                        : 'rgba(249, 115, 22, 0.1)'
                        } 0%, transparent 70%)`,
                    borderRadius: '50%'
                }} />

                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '12px'
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}>
                        <div style={{
                            width: '12px',
                            height: '12px',
                            background: `linear-gradient(135deg, ${metrics.todayProgress.isCompleted
                                ? '#22c55e 0%, #16a34a 100%'
                                : '#f97316 0%, #ea580c 100%'
                                }`,
                            borderRadius: '50%',
                            boxShadow: `0 0 12px ${metrics.todayProgress.isCompleted
                                ? 'rgba(34, 197, 94, 0.5)'
                                : 'rgba(249, 115, 22, 0.5)'
                                }`,
                            position: 'relative'
                        }}>
                            <div style={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                width: '6px',
                                height: '6px',
                                background: 'white',
                                borderRadius: '50%',
                                opacity: 0.8
                            }} />
                        </div>
                        <span style={{
                            fontSize: '13px',
                            fontWeight: '600',
                            color: theme.theme.colors.text,
                            letterSpacing: '-0.2px'
                        }}>
                            Today's Progress
                        </span>
                    </div>

                    {/* Ultra-Posh Today's Progress Display */}
                    <div style={{
                        background: `linear-gradient(135deg, ${metrics.todayProgress.isCompleted
                            ? '#22c55e 0%, #16a34a 100%'
                            : '#f97316 0%, #ea580c 100%'
                            }`,
                        color: 'white',
                        padding: '8px 16px',
                        borderRadius: '12px',
                        fontSize: '14px',
                        fontWeight: '700',
                        boxShadow: `0 4px 12px ${metrics.todayProgress.isCompleted
                            ? 'rgba(34, 197, 94, 0.3)'
                            : 'rgba(249, 115, 22, 0.3)'
                            }`,
                        position: 'relative',
                        overflow: 'hidden'
                    }}>
                        {/* Shine Effect */}
                        <div style={{
                            position: 'absolute',
                            top: '0',
                            left: '-100%',
                            width: '100%',
                            height: '100%',
                            background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
                            animation: 'shine 2s infinite'
                        }} />

                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            position: 'relative',
                            zIndex: 1
                        }}>
                            <span style={{
                                fontSize: '16px',
                                filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))'
                            }}>
                                {metrics.todayProgress.isCompleted ? '✅' : '🔥'}
                            </span>
                            <span>{metrics.todayProgress.progressPercentage.toFixed(0)}%</span>
                        </div>
                    </div>
                </div>

                {/* Today's Progress Details */}
                <div style={{
                    background: `rgba(${metrics.todayProgress.isCompleted ? '34, 197, 94' : '249, 115, 22'}, 0.05)`,
                    borderRadius: '8px',
                    padding: '8px 12px',
                    border: `1px solid rgba(${metrics.todayProgress.isCompleted ? '34, 197, 94' : '249, 115, 22'}, 0.1)`
                }}>
                    <div style={{
                        fontSize: '11px',
                        color: theme.theme.colors.text,
                        fontWeight: '500',
                        lineHeight: '1.4'
                    }}>
                        {formatValue(metrics.todayProgress.currentValue, challenge.metric, unit)} completed out of {formatValue(metrics.todayProgress.targetValue, challenge.metric, unit)}
                        {metrics.todayProgress.pointsEarned > 0 && (
                            <span style={{
                                marginLeft: '8px',
                                color: metrics.todayProgress.isCompleted ? '#22c55e' : '#f97316',
                                fontWeight: '700'
                            }}>
                                (+{metrics.todayProgress.pointsEarned} points earned)
                            </span>
                        )}
                    </div>
                </div>

                {/* Ultra-Posh Progress Bar */}
                <div style={{
                    width: '100%',
                    height: '8px',
                    background: `rgba(${metrics.todayProgress.isCompleted ? '34, 197, 94' : '249, 115, 22'}, 0.1)`,
                    borderRadius: '4px',
                    overflow: 'hidden',
                    position: 'relative'
                }}>
                    <div
                        style={{
                            height: '100%',
                            background: `linear-gradient(90deg, ${metrics.todayProgress.isCompleted
                                ? '#22c55e 0%, #16a34a 100%'
                                : '#f97316 0%, #ea580c 100%'
                                }`,
                            borderRadius: '4px',
                            transition: 'width 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
                            width: `${Math.min(100, metrics.todayProgress.progressPercentage)}%`,
                            boxShadow: `0 0 12px ${metrics.todayProgress.isCompleted
                                ? 'rgba(34, 197, 94, 0.4)'
                                : 'rgba(249, 115, 22, 0.4)'
                                }`
                        }}
                    />
                </div>

                {metrics.todayProgress.pointsEarned > 0 && (
                    <div style={{
                        marginTop: '12px',
                        padding: '8px 12px',
                        background: `rgba(${metrics.todayProgress.isCompleted ? '34, 197, 94' : '249, 115, 22'}, 0.1)`,
                        borderRadius: '8px',
                        border: `1px solid rgba(${metrics.todayProgress.isCompleted ? '34, 197, 94' : '249, 115, 22'}, 0.2)`
                    }}>
                        <div style={{
                            fontSize: '12px',
                            color: metrics.todayProgress.isCompleted ? '#22c55e' : '#f97316',
                            fontWeight: '600',
                            textAlign: 'center'
                        }}>
                            {metrics.todayProgress.isCompleted ? '🎉 Completed!' : '🚀 Progress!'} +{metrics.todayProgress.pointsEarned} points earned
                        </div>
                    </div>
                )}
            </div>

            {/* Detailed Breakdown (Optional) */}
            {showDetailedBreakdown && (
                <div className="detailed-breakdown border-t border-gray-200 dark:border-gray-700 pt-4">
                    <div className="flex flex-wrap gap-3 justify-center">
                        {/* Completed Days Badge */}
                        <div className="relative group">
                            <div className="bg-gradient-to-r from-emerald-500 to-teal-600 dark:from-emerald-600 dark:to-teal-700 px-4 py-2 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                                <div className="flex items-center space-x-2">
                                    <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                                    <span className="text-white font-bold text-sm">
                                        {metrics.overallProgress.completedDays}
                                    </span>
                                    <span className="text-emerald-100 text-xs font-medium">Days Done</span>
                                </div>
                            </div>
                        </div>

                        {/* Success Rate Badge */}
                        <div className="relative group">
                            <div className={`px-4 py-2 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 ${metrics.overallProgress.successRate >= 80
                                ? 'bg-gradient-to-r from-green-500 to-emerald-600 dark:from-green-600 dark:to-emerald-700'
                                : metrics.overallProgress.successRate >= 50
                                    ? 'bg-gradient-to-r from-yellow-500 to-orange-600 dark:from-yellow-600 dark:to-orange-700'
                                    : 'bg-gradient-to-r from-red-500 to-pink-600 dark:from-red-600 dark:to-pink-700'
                                }`}>
                                <div className="flex items-center space-x-2">
                                    <div className={`w-2 h-2 rounded-full animate-pulse ${metrics.overallProgress.successRate >= 80 ? 'bg-white' :
                                        metrics.overallProgress.successRate >= 50 ? 'bg-white' : 'bg-white'
                                        }`}></div>
                                    <span className="text-white font-bold text-sm">
                                        {metrics.overallProgress.successRate.toFixed(1)}%
                                    </span>
                                    <span className="text-white/80 text-xs font-medium">Success</span>
                                </div>
                            </div>
                        </div>

                        {/* Days Elapsed Badge */}
                        <div className="relative group">
                            <div className="bg-gradient-to-r from-blue-500 to-indigo-600 dark:from-blue-600 dark:to-indigo-700 px-4 py-2 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                                <div className="flex items-center space-x-2">
                                    <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                                    <span className="text-white font-bold text-sm">
                                        {metrics.timeline.daysElapsed}
                                    </span>
                                    <span className="text-blue-100 text-xs font-medium">Elapsed</span>
                                </div>
                            </div>
                        </div>

                        {/* Days Remaining Badge */}
                        <div className="relative group">
                            <div className="bg-gradient-to-r from-purple-500 to-violet-600 dark:from-purple-600 dark:to-violet-700 px-4 py-2 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                                <div className="flex items-center space-x-2">
                                    <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                                    <span className="text-white font-bold text-sm">
                                        {metrics.timeline.daysRemaining}
                                    </span>
                                    <span className="text-purple-100 text-xs font-medium">Left</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EnhancedProgressMetricsWeb;
